import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Storefront, MapPin, Star, ArrowRight, Megaphone } from "@phosphor-icons/react";
import { resolveMedia } from "@/lib/media";
import { InViewVideo } from "@/components/InViewVideo";
import { isFeedMuted } from "@/components/FeedItem";
import { cachedLocation } from "@/lib/nearby";

// One view per business per app session, counted when the card is actually on screen.
const viewed = new Set();
const track = (business_id, kind) => {
  const loc = cachedLocation();
  return api.post("/market/spotlight/event", {
    business_id, kind, places: loc.places.slice(0, 6),
    ...(loc.lat != null && loc.lng != null ? { lat: loc.lat, lng: loc.lng } : {}),
  }).catch(() => {});
};

export function SpotlightCard({ biz }) {
  const navigate = useNavigate();
  const ref = useRef(null);
  useEffect(() => {
    if (!biz || viewed.has(biz.business_id) || !ref.current) return;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting || viewed.has(biz.business_id)) return;
      viewed.add(biz.business_id);
      track(biz.business_id, "view");
      io.disconnect();
    }, { threshold: 0.6 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [biz]);
  if (!biz) return <PromoteInviteCard onOpen={() => navigate("/market?tab=business")} />;
  const cover = biz.media?.find((m) => m.type === "video") || biz.media?.[0];
  const open = () => {
    track(biz.business_id, "tap");
    navigate(`/market?tab=locations&loc=${encodeURIComponent(biz.location.trim().toLowerCase())}`);
  };

  return (
    <div ref={ref} data-testid="market-spotlight-card" className="relative w-full h-full overflow-hidden bg-gradient-to-b from-sky-300 via-sky-400 to-cyan-500 flex flex-col items-center justify-center p-6 text-center">
      <div className="absolute inset-0 opacity-20 bg-[linear-gradient(transparent_95%,white_95%),linear-gradient(90deg,transparent_95%,white_95%)] bg-[length:28px_28px]" />
      <p className="relative inline-flex items-center gap-2 text-xs font-black uppercase tracking-[0.3em] text-sky-950">
        <Storefront size={16} weight="fill" /> Promoted · Tainapp Market
      </p>
      {cover && (
        cover.type === "video"
          ? <InViewVideo src={resolveMedia(cover.url)} soundOn={!isFeedMuted()} testId="spotlight-video" className="relative mt-5 w-56 h-56 object-cover rounded-3xl ring-4 ring-white shadow-2xl" />
          : <img src={resolveMedia(cover.url)} alt="" className="relative mt-5 w-56 h-56 object-cover rounded-3xl ring-4 ring-white shadow-2xl" />
      )}
      <h3 data-testid="market-spotlight-name" className="relative mt-5 font-heading text-4xl font-black text-white drop-shadow-[0_2px_8px_rgba(8,47,73,0.5)]">{biz.name}</h3>
      <p className="relative mt-2 text-sky-950 font-semibold flex items-center gap-1">
        <MapPin size={14} weight="fill" /> {biz.location}{biz.category ? ` · ${biz.category}` : ""}
      </p>
      {biz.rating_count > 0 && (
        <p className="relative mt-1 font-black text-amber-300 drop-shadow flex items-center gap-1">
          <Star size={16} weight="fill" /> {biz.rating_avg.toFixed(1)} <span className="text-white/90 font-semibold">({biz.rating_count} ratings)</span>
        </p>
      )}
      {biz.description && <p className="relative mt-3 max-w-xs text-sm text-white/95 line-clamp-3">{biz.description}</p>}
      <button data-testid="market-spotlight-open" onClick={open}
        className="relative mt-6 h-12 px-6 rounded-full bg-sky-950 text-white font-heading font-black inline-flex items-center gap-2 transition-transform hover:scale-105 active:scale-95">
        Open in Tainapp Market <ArrowRight size={18} weight="bold" />
      </button>
    </div>
  );
}

function PromoteInviteCard({ onOpen }) {
  return (
    <div data-testid="promote-invite-card" className="relative w-full h-full overflow-hidden bg-gradient-to-b from-sky-300 via-sky-400 to-cyan-500 flex flex-col items-center justify-center p-6 text-center">
      <div className="absolute inset-0 opacity-20 bg-[linear-gradient(transparent_95%,white_95%),linear-gradient(90deg,transparent_95%,white_95%)] bg-[length:28px_28px]" />
      <div className="relative rounded-full bg-white/90 p-6 shadow-2xl"><Megaphone size={64} weight="fill" className="text-sky-500" /></div>
      <h3 className="relative mt-6 font-heading text-4xl font-black text-white drop-shadow-[0_2px_8px_rgba(8,47,73,0.5)]">Promote your business here</h3>
      <p className="relative mt-3 max-w-xs text-sky-950 font-semibold">Reach people near you on the Tainapp feed. Pick your locations and how long — you&apos;re live instantly.</p>
      <button data-testid="promote-invite-open" onClick={onOpen}
        className="relative mt-6 h-12 px-6 rounded-full bg-sky-950 text-white font-heading font-black inline-flex items-center gap-2 transition-transform hover:scale-105 active:scale-95">
        Promote now <ArrowRight size={18} weight="bold" />
      </button>
    </div>
  );
}
